import React, {useContext, useState, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {
  PractitionerCreateAppointmentResponse,
  PractitionersPatient,
} from '@common/models'
import {
  MISSING_ID,
  currentYyMm,
  getCalendarMatrix,
  buildSlotsByDate,
  yymmToYearMonth,
} from '@common/utils'
import {
  formatDateLegend,
  formatDateTimeLocal,
  parseDateTimeLocal,
  parseSlotDateTime,
} from '@common/utils/dates'

import {FindPatientCard} from './FindPatientCard'
import {AppointmentDetailsCard} from './AddAppointmentModal.styled'

import {
  Grid,
  Column,
  SelectedGlobal,
} from '~/components/bookings/BookingsPortalPage.styled'
import {CalendarPanel} from '~/components/bookings/CalendarPanel'
import {DayTimePanel} from '~/components/bookings/DayTimePanel'
import {SideModal} from '~/components/general/modal/SideModal'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Input} from '~/components/general/input/Input'
import {Checkbox} from '~/components/general/checkbox/Checkbox'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {PractitionerContext} from '~/components/profile/PractitionerContext'
import {useCreatePatientAppointment} from '~/api'
import {useGetPractitionerAvailabilities} from '~/api/practitioner'
import {useMonthNavigation} from '~/hooks/useMonthNavigation'
import {useWeekdayShorts} from '~/hooks/useWeekdayShorts'

interface AddAppointmentModalProps {
  isVisible: boolean
  onClose: () => void
}

type AvailabilityDay = {date: string; slots: string[]}

const AddAppointmentModal = ({
  isVisible,
  onClose,
}: AddAppointmentModalProps) => {
  const navigate = useNavigate()
  const {t} = useTranslation()
  const {selectedEmployment} = useContext(PractitionerContext)
  const [selectedPatient, setSelectedPatient] =
    useState<PractitionersPatient | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [apiMonthSpec, setApiMonthSpec] = useState<string>('FIRST')
  const [calendarYyMm, setCalendarYyMm] = useState<string>(currentYyMm())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [createCustomSlot, setCreateCustomSlot] = useState<boolean>(false)
  const [customDateTime, setCustomDateTime] = useState<string>(() => {
    const timeNow = new Date()
    const lastTenMinRounded = new Date(
      timeNow.setMinutes(Math.floor(timeNow.getMinutes() / 10) * 10),
    )
    return formatDateTimeLocal(lastTenMinRounded)
  })

  const {mutate, isPending: isUpdating} = useCreatePatientAppointment({
    facilityId: selectedEmployment?.facilityId || MISSING_ID,
  })

  const locations = selectedEmployment?.departments || []
  const showLocationSelector = locations.length > 0
  let effectiveLocation: string | null = null
  if (showLocationSelector) {
    effectiveLocation = selectedLocation
  } else if (locations.length === 0) {
    effectiveLocation = ''
  }

  const {
    data: avail,
    isLoading: isLoadingAvailabilities,
    isFetching: isFetchingAvailabilities,
  } = useGetPractitionerAvailabilities(
    {
      facilityId: selectedEmployment?.facilityId || MISSING_ID,
      serviceName: effectiveLocation || '',
      monthSpec: apiMonthSpec,
    },
    {
      enabled:
        isVisible &&
        !!effectiveLocation &&
        !!selectedEmployment?.facilityId &&
        !createCustomSlot,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    },
  )

  const days = useMemo<AvailabilityDay[]>(
    () => (avail?.days as AvailabilityDay[]) ?? [],
    [avail?.days],
  )

  const didSetCalendarFromApiRef = useRef(false)

  useEffect(() => {
    if (!isVisible) {
      setSelectedPatient(null)
      setSelectedLocation(null)
      setApiMonthSpec('FIRST')
      setCalendarYyMm(currentYyMm())
      setSelectedDate(null)
      setSelectedTime(null)
      setCreateCustomSlot(false)
      didSetCalendarFromApiRef.current = false
    }
  }, [isVisible])

  useEffect(() => {
    if (!avail || !isVisible || !selectedLocation) return
    if (didSetCalendarFromApiRef.current) return
    const yymm = avail?.yearMonth
    if (yymm) setCalendarYyMm(yymm)
    didSetCalendarFromApiRef.current = true
  }, [avail, isVisible, selectedLocation])

  useEffect(() => {
    if (showLocationSelector && !selectedLocation) {
      setSelectedDate(null)
      setSelectedTime(null)
      setApiMonthSpec('FIRST')
      setCalendarYyMm(currentYyMm())
      didSetCalendarFromApiRef.current = false
    }
  }, [selectedLocation, showLocationSelector])

  const cells = useMemo(() => getCalendarMatrix(calendarYyMm), [calendarYyMm])

  const selectedDateLegend = useMemo(
    () =>
      formatDateLegend(selectedDate || undefined, selectedTime || undefined),
    [selectedDate, selectedTime],
  )

  const slotsByDate = useMemo(() => buildSlotsByDate(days), [days])

  useEffect(() => {
    if (!isVisible || !effectiveLocation || createCustomSlot) return
    const first = days.find((d: AvailabilityDay) => (d.slots?.length || 0) > 0)
    if (first) {
      setSelectedDate(first.date)
    } else if (avail) {
      const {year, month} = yymmToYearMonth(calendarYyMm)
      const firstDayOfMonth = `${year}/${String(month).padStart(2, '0')}/01`
      setSelectedDate(firstDayOfMonth)
    }
    setSelectedTime(null)
  }, [
    isVisible,
    effectiveLocation,
    days,
    avail,
    calendarYyMm,
    createCustomSlot,
  ])

  const weekdayShorts = useWeekdayShorts()

  const {gotoPrevMonth, gotoNextMonth, monthLabel, disablePrev} =
    useMonthNavigation({
      currentYyMm: calendarYyMm,
      onMonthChange: (yyMm) => {
        setCalendarYyMm(yyMm)
        setApiMonthSpec(yyMm)
      },
    })

  const handleCreateAppointment = () => {
    if (!selectedPatient) {
      return
    }

    let appointmentDate: Date

    if (createCustomSlot) {
      appointmentDate = parseDateTimeLocal(customDateTime)
    } else if (selectedDate && selectedTime) {
      appointmentDate = parseSlotDateTime(selectedDate, selectedTime)
    } else {
      return
    }

    mutate(
      {
        patientId: selectedPatient.patientId,
        appointmentDate: appointmentDate.toISOString(),
        department: selectedLocation || undefined,
      },
      {
        onSuccess: (response: PractitionerCreateAppointmentResponse) => {
          navigate(`${response.appointmentId}/${selectedPatient.patientId}`)
        },
      },
    )
  }

  const canCreate =
    !isUpdating &&
    !!selectedPatient &&
    (selectedEmployment?.departments.length === 0 || !!selectedLocation) &&
    (createCustomSlot ? !!customDateTime : !!selectedDate && !!selectedTime)

  const showProcessingOverlay =
    isUpdating ||
    ((isFetchingAvailabilities || isLoadingAvailabilities) &&
      !!effectiveLocation &&
      !createCustomSlot)

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      modalSize="wide"
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {t('Add Appointment')}
          </Typography>
        </Container>
      }>
      <FindPatientCard
        onSelectPatient={(patient) => {
          setSelectedPatient(patient)
        }}
      />
      {selectedPatient && (
        <AppointmentDetailsCard>
          <ProcessingOverlay isVisible={showProcessingOverlay} />
          <Container flex direction="column" spacing={2}>
            <Typography size="md" weight="medium">
              {t('Appointment Details')}
            </Typography>

            {showLocationSelector && (
              <Container flex direction="column" spacing={1}>
                <Typography>{t('Location')}</Typography>
                <FilteredSelect
                  options={locations.map((location) => ({
                    label: location,
                    value: location,
                  }))}
                  onChange={(value) => {
                    setSelectedLocation(value || null)
                    setCreateCustomSlot(false)
                    setSelectedDate(null)
                    setSelectedTime(null)
                  }}
                />
              </Container>
            )}

            {(!showLocationSelector || selectedLocation) && (
              <>
                {!createCustomSlot && (
                  <>
                    {selectedDate && (
                      <SelectedGlobal>
                        <Typography size="xl" weight="bold">
                          {selectedDateLegend}
                        </Typography>
                      </SelectedGlobal>
                    )}
                    <Grid>
                      <Column>
                        <CalendarPanel
                          dateTitle={t('DATE')}
                          monthLabel={monthLabel}
                          weekdayShorts={weekdayShorts}
                          cells={cells}
                          slotsByDate={slotsByDate}
                          selectedDate={selectedDate}
                          onSelectDate={(dateKey) => {
                            setSelectedDate(dateKey)
                            setSelectedTime(null)
                          }}
                          disablePrev={disablePrev}
                          onPrevMonth={gotoPrevMonth}
                          onNextMonth={gotoNextMonth}
                        />
                      </Column>
                      <Column>
                        <DayTimePanel
                          title={t('TIME')}
                          selectedDate={selectedDate}
                          selectedDateSlots={
                            selectedDate
                              ? slotsByDate.get(selectedDate) || []
                              : []
                          }
                          selectedTime={selectedTime}
                          onSelectTime={(time) => setSelectedTime(time)}
                          isLoading={isUpdating}
                          timeZoneNotice={undefined}
                          noAvailabilityText={t(
                            'There is no availability on this date. Please choose another one.',
                          )}
                        />
                      </Column>
                    </Grid>
                  </>
                )}

                <Container flex direction="column" spacing={1}>
                  <Checkbox
                    checked={createCustomSlot}
                    onChange={() => {
                      const newValue = !createCustomSlot
                      setCreateCustomSlot(newValue)
                      if (newValue) {
                        setSelectedDate(null)
                        setSelectedTime(null)
                      }
                    }}
                    label={t('Define a new slot')}
                  />
                </Container>

                {createCustomSlot && (
                  <Container flex direction="column" spacing={1}>
                    <Typography>{t('Date and time')}</Typography>
                    <Input
                      type="datetime-local"
                      value={customDateTime}
                      onChange={(e) => {
                        setCustomDateTime(e.target.value)
                      }}
                    />
                  </Container>
                )}
              </>
            )}

            <Container flex justify="flex-end">
              <Button onClick={handleCreateAppointment} disabled={!canCreate}>
                {t('Create Appointment')}
              </Button>
            </Container>
          </Container>
        </AppointmentDetailsCard>
      )}
    </SideModal>
  )
}

export default AddAppointmentModal
